// adminPage — pure validation + outcome map for the Phase 6 Task 8 管理画面.
//
// AdminPage / RoomSettingsModal の credential 管理タブで使う「保存ボタンの結果」
// 状態 union を作る. 既存の `loginForm.ts` / `roomSettingsForm.ts` と同じ流儀で
// DOM 引かない pure function に切り出して vitest node-only で網羅する.
//
// 確定方針 (consult 2026-05-04 AK〜AS):
// - AM. credential 発行 permissions = 個別 CB 3 つ (KICK / IMAGEPASTE / CLEAR).
//        Phase 7 Task 7-5 で CLEAR (1 << 2) 追加、ALL_PERMS = 7 に拡張.
//        bitflag 変換は `application/uiFeedback.ts` 側 (UI 共通). validation
//        は本モジュール (server `CredentialPermissionsSchema = z.number().int()
//        .gte(0).lte(ALL_PERMS=7)` と同じ範囲).
// - AR. 空欄ルーム名は `(無題)` フォールバック表示. AdminPage / RoomSettingsModal
//        の両方で同じ helper を使う.
// - 共通 outcome 流儀: 'ok' / 'unauthorized' / 'invalid_body' / 'conflict' /
//   'rate_limited' / 'unknown' / 'not_found' (delete 系).
// - 401 unauthorized は AdminPage 側で「ログイン期限切れ → /login 画面に navigate」
//   判断材料に使うため、`shouldNavigateToLogin(outcome)` ヘルパを提供する.

import { ALL_PERMS } from '@oekaki/protocol';

import type {
  ActiveRoomLookupResult,
  AdminRoomSummary,
  BindSessionEmailOutcome,
  CreateCredentialOutcome,
  CredentialSummary,
  DeleteCredentialOutcome,
  DeleteRoomOutcome,
  IssueAdminTokensOutcome,
  ListAdminRoomsOutcome,
  ListCredentialsOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { formatMs } from './uiFeedback.js';

/* -----------------------------------------------------------------------------
 * Constants — credential 発行 form の UI 制約
 *
 * Server 側 (`createDispatcherApp.ts`):
 *   - CredentialLoginIdSchema      = z.string().trim().min(1).max(32)
 *   - CredentialPasswordSchema     = z.string().min(8).max(128)
 *   - CredentialDisplayNameSchema  = z.string().trim().min(1).max(64).optional()
 *   - CredentialPermissionsSchema  = z.number().int().gte(0).lte(ALL_PERMS=7)
 *     (Phase 7 Task 7-5 で CLEAR=4 追加、ALL_PERMS=3→7)
 *
 * UI 側は server と同じ範囲で early validate (false negative 寄り).
 * --------------------------------------------------------------------------- */

export const CREDENTIAL_LOGIN_ID_MIN_LENGTH = 1;
export const CREDENTIAL_LOGIN_ID_MAX_LENGTH = 32;
export const CREDENTIAL_PASSWORD_MIN_LENGTH = 8;
export const CREDENTIAL_PASSWORD_MAX_LENGTH = 128;
export const CREDENTIAL_DISPLAY_NAME_MAX_LENGTH = 64;

/* -----------------------------------------------------------------------------
 * Field validation
 *
 * 個別フィールドごとに `validateXxx(raw): { outcome: 'ok' | 'failed', ... }` を
 * 提供する. 既存 `roomSettingsForm.ts` / `loginForm.ts` のパターン踏襲.
 * --------------------------------------------------------------------------- */

export type CredentialLoginIdError = 'empty' | 'too-long';
export type CredentialPasswordError = 'too-short' | 'too-long';
export type CredentialDisplayNameError = 'too-long';
export type CredentialPermissionsError = 'not-integer' | 'out-of-range' | 'not-finite';

export type CredentialLoginIdValidation =
  | { readonly outcome: 'ok'; readonly value: string }
  | { readonly outcome: 'failed'; readonly error: CredentialLoginIdError };

export const validateCredentialLoginId = (raw: string): CredentialLoginIdValidation => {
  const trimmed = raw.trim();
  if (trimmed.length < CREDENTIAL_LOGIN_ID_MIN_LENGTH) {
    return { outcome: 'failed', error: 'empty' };
  }
  if (trimmed.length > CREDENTIAL_LOGIN_ID_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  return { outcome: 'ok', value: trimmed };
};

export type CredentialPasswordValidation =
  | { readonly outcome: 'ok'; readonly value: string }
  | { readonly outcome: 'failed'; readonly error: CredentialPasswordError };

export const validateCredentialPassword = (raw: string): CredentialPasswordValidation => {
  // passphrase は前後空白も意味あり得るので trim しない.
  if (raw.length < CREDENTIAL_PASSWORD_MIN_LENGTH) {
    return { outcome: 'failed', error: 'too-short' };
  }
  if (raw.length > CREDENTIAL_PASSWORD_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  return { outcome: 'ok', value: raw };
};

export type CredentialDisplayNameValidation =
  /** 空欄なら undefined (server 側 optional に流す). */
  | { readonly outcome: 'ok'; readonly value: string | undefined }
  | { readonly outcome: 'failed'; readonly error: CredentialDisplayNameError };

export const validateCredentialDisplayName = (raw: string): CredentialDisplayNameValidation => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { outcome: 'ok', value: undefined };
  if (trimmed.length > CREDENTIAL_DISPLAY_NAME_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  return { outcome: 'ok', value: trimmed };
};

export type CredentialPermissionsValidation =
  | { readonly outcome: 'ok'; readonly value: number }
  | { readonly outcome: 'failed'; readonly error: CredentialPermissionsError };

export const validateCredentialPermissions = (raw: number): CredentialPermissionsValidation => {
  if (!Number.isFinite(raw)) return { outcome: 'failed', error: 'not-finite' };
  if (!Number.isInteger(raw)) return { outcome: 'failed', error: 'not-integer' };
  if (raw < 0 || raw > ALL_PERMS) return { outcome: 'failed', error: 'out-of-range' };
  return { outcome: 'ok', value: raw };
};

/* -----------------------------------------------------------------------------
 * Aggregate credential form validation
 *
 * 4 フィールドまとめて検証して、submit body を作る. UI 側 (RoomSettingsModal の
 * credential タブ) は本関数の outcome を見て送信 / エラー表示を分岐.
 * --------------------------------------------------------------------------- */

export interface CredentialFormValues {
  readonly loginId: string;
  readonly password: string;
  readonly displayName: string;
  readonly permissions: number;
}

export interface CredentialFormBody {
  readonly loginId: string;
  readonly password: string;
  readonly displayName?: string;
  readonly permissions: number;
}

export type CredentialFormValidation =
  | { readonly outcome: 'ok'; readonly body: CredentialFormBody }
  | {
      readonly outcome: 'failed';
      readonly errors: {
        readonly loginId?: CredentialLoginIdError;
        readonly password?: CredentialPasswordError;
        readonly displayName?: CredentialDisplayNameError;
        readonly permissions?: CredentialPermissionsError;
      };
    };

export const validateCredentialForm = (values: CredentialFormValues): CredentialFormValidation => {
  const errors: {
    loginId?: CredentialLoginIdError;
    password?: CredentialPasswordError;
    displayName?: CredentialDisplayNameError;
    permissions?: CredentialPermissionsError;
  } = {};

  const lid = validateCredentialLoginId(values.loginId);
  const pw = validateCredentialPassword(values.password);
  const dn = validateCredentialDisplayName(values.displayName);
  const pm = validateCredentialPermissions(values.permissions);

  if (lid.outcome !== 'ok') errors.loginId = lid.error;
  if (pw.outcome !== 'ok') errors.password = pw.error;
  if (dn.outcome !== 'ok') errors.displayName = dn.error;
  if (pm.outcome !== 'ok') errors.permissions = pm.error;

  if (Object.keys(errors).length > 0) {
    return { outcome: 'failed', errors };
  }
  // TS が outcome === 'ok' を絞れるように分岐ガードを再評価.
  if (lid.outcome !== 'ok' || pw.outcome !== 'ok' || dn.outcome !== 'ok' || pm.outcome !== 'ok') {
    return { outcome: 'failed', errors };
  }

  const body: CredentialFormBody = {
    loginId: lid.value,
    password: pw.value,
    ...(dn.value !== undefined ? { displayName: dn.value } : {}),
    permissions: pm.value,
  };
  return { outcome: 'ok', body };
};

/* -----------------------------------------------------------------------------
 * Field error → user-facing string
 * --------------------------------------------------------------------------- */

export const credentialLoginIdErrorText = (err: CredentialLoginIdError): string => {
  switch (err) {
    case 'empty':
      return 'ログイン ID を入力してください。';
    case 'too-long':
      return `ログイン ID は ${CREDENTIAL_LOGIN_ID_MAX_LENGTH} 文字以内で入力してください。`;
  }
};

export const credentialPasswordErrorText = (err: CredentialPasswordError): string => {
  switch (err) {
    case 'too-short':
      return `パスワードは ${CREDENTIAL_PASSWORD_MIN_LENGTH} 文字以上で入力してください。`;
    case 'too-long':
      return `パスワードは ${CREDENTIAL_PASSWORD_MAX_LENGTH} 文字以内で入力してください。`;
  }
};

export const credentialDisplayNameErrorText = (err: CredentialDisplayNameError): string => {
  switch (err) {
    case 'too-long':
      return `表示名は ${CREDENTIAL_DISPLAY_NAME_MAX_LENGTH} 文字以内で入力してください。`;
  }
};

export const credentialPermissionsErrorText = (err: CredentialPermissionsError): string => {
  switch (err) {
    case 'not-integer':
    case 'not-finite':
      return '権限の値が正しくありません。';
    case 'out-of-range':
      return `権限は 0..${ALL_PERMS} の範囲で指定してください。`;
  }
};

/* -----------------------------------------------------------------------------
 * Display helpers
 * --------------------------------------------------------------------------- */

/** AR. 空欄ルーム名フォールバック. */
export const DISPLAY_ROOM_NAME_FALLBACK = '(無題)';
export const displayRoomName = (name: string): string => {
  const trimmed = name.trim();
  return trimmed.length === 0 ? DISPLAY_ROOM_NAME_FALLBACK : trimmed;
};

/* -----------------------------------------------------------------------------
 * Outcome → screen-side state
 *
 * 各 endpoint の outcome union を AdminPage 側 union (snake_case 慣例で揃える、
 * roomSettingsForm.ts と同じ) に潰す.
 *
 * - 'ok'             : 成功
 * - 'unauthorized'   : ログイン期限切れ → /login 画面に誘導
 * - 'invalid_body'   : 通常起こらない (UI 側で validate 済) が出たら fallback
 * - 'conflict'       : credential 発行で 409 (loginId 重複)
 * - 'not_found'      : 削除系で room / credential が消えた
 * - 'rate_limited'   : credential 発行で 429 (server で rate limit 対象外だが forward-compat)
 * - 'unknown'        : network / invalid / その他 http-status
 * --------------------------------------------------------------------------- */

const UNAUTHORIZED_MESSAGE = 'ログインの有効期限が切れています。ログイン画面に移動します。';
const INVALID_BODY_MESSAGE = 'リクエストの形式が正しくありません。入力をご確認ください。';
const UNKNOWN_MESSAGE = 'エラーが発生しました。時間を置いて再度お試しください。';
const NOT_FOUND_MESSAGE = '対象が見つかりませんでした (既に削除されている可能性があります)。';
const CONFLICT_MESSAGE = 'このログイン ID は既に使用されています。別の ID を入力してください。';

export type ListAdminRoomsResult =
  | { readonly outcome: 'ok'; readonly rooms: readonly AdminRoomSummary[] }
  | { readonly outcome: 'unauthorized'; readonly message: string }
  | { readonly outcome: 'unknown'; readonly message: string };

export type ListCredentialsResult =
  | { readonly outcome: 'ok'; readonly credentials: readonly CredentialSummary[] }
  | { readonly outcome: 'unauthorized'; readonly message: string }
  | { readonly outcome: 'not_found'; readonly message: string }
  | { readonly outcome: 'unknown'; readonly message: string };

export interface AdminActionResult {
  readonly outcome:
    | 'ok'
    | 'unauthorized'
    | 'invalid_body'
    | 'conflict'
    | 'not_found'
    | 'rate_limited'
    | 'unknown';
  readonly message?: string;
}

/** Map `listAdminRooms` outcome → screen-side state. */
export const mapListAdminRoomsOutcome = (out: ListAdminRoomsOutcome): ListAdminRoomsResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', rooms: out.rooms };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'unknown':
      return { outcome: 'unknown', message: out.message ?? UNKNOWN_MESSAGE };
  }
};

/** Map `listCredentials` outcome → screen-side state (for the modal panel). */
export const mapListCredentialsOutcome = (out: ListCredentialsOutcome): ListCredentialsResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', credentials: out.credentials };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'not-found':
      return { outcome: 'not_found', message: NOT_FOUND_MESSAGE };
    case 'unknown':
      return { outcome: 'unknown', message: out.message ?? UNKNOWN_MESSAGE };
  }
};

export const mapCreateCredentialOutcome = (out: CreateCredentialOutcome): AdminActionResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', message: '認証アカウントを作成しました。' };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'invalid-body':
      return { outcome: 'invalid_body', message: INVALID_BODY_MESSAGE };
    case 'conflict':
      return { outcome: 'conflict', message: CONFLICT_MESSAGE };
    case 'rate-limited':
      return {
        outcome: 'rate_limited',
        message: `操作制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      return { outcome: 'unknown', message: out.message ?? UNKNOWN_MESSAGE };
  }
};

export const mapDeleteCredentialOutcome = (out: DeleteCredentialOutcome): AdminActionResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', message: '認証アカウントを削除しました。' };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'not-found':
      return { outcome: 'not_found', message: NOT_FOUND_MESSAGE };
    case 'unknown':
      return { outcome: 'unknown', message: out.message ?? UNKNOWN_MESSAGE };
  }
};

export const mapDeleteRoomOutcome = (out: DeleteRoomOutcome): AdminActionResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', message: 'ルームを削除しました。' };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'not-found':
      return { outcome: 'not_found', message: NOT_FOUND_MESSAGE };
    case 'unknown':
      return { outcome: 'unknown', message: out.message ?? UNKNOWN_MESSAGE };
  }
};

/**
 * `unauthorized` は cross-room cookie session が切れた状態. AdminPage 側で
 * 「ログイン画面に navigate」する判断材料.
 */
export const shouldNavigateToLogin = (outcome: string): boolean => outcome === 'unauthorized';

/* -----------------------------------------------------------------------------
 * issueAdminTokens 結果 → AdminTokenStore への保存
 *
 * 2026-05-23 W2 公開前追加. AdminPage 初期化時 (= magic link で email 認証
 * 直後) に `POST /admin/admin-tokens/issue` を叩いて受け取った tokens を
 * localStorage に保存する. ここは pure helper として切り出しておき、AdminPage
 * 側からは「outcome を受け取って save する」を呼ぶだけ.
 *
 * - 'ok'           → 各 entry を save() に流す. 保存件数を返す.
 * - 'unauthorized' → 0. AdminPage 側で listAdminRooms 401 で /login へ navigate
 *                    する流れに乗るので、ここでは何もしない.
 * - 'rate-limited' → 0. UI 表示には影響させない (= サイレントフェイル).
 * - 'unknown'      → 0. 同上.
 * --------------------------------------------------------------------------- */

export interface AdminTokenSaver {
  /** AdminTokenStore.save と同形 (= per-room set, idempotent). */
  save(roomId: string, token: string): void;
}

/**
 * `issueAdminTokens` outcome を AdminTokenStore に反映する.
 *
 * @returns 実際に save() を呼んだ件数. ログ用 (= 0 件のときは「サイレントフェイル
 *          扱い」と区別がつきにくいので caller でログを出すかどうかの判断材料).
 */
export const applyIssuedAdminTokens = (
  out: IssueAdminTokensOutcome,
  store: AdminTokenSaver,
): number => {
  if (out.outcome !== 'ok') return 0;
  let saved = 0;
  for (const t of out.tokens) {
    // AdminTokenStore.save は空文字 token を no-op で弾く流儀だが、ここでも
    // 念のため早期 continue (= 副作用 + count をブレさせない).
    if (t.roomId.length === 0 || t.adminToken.length === 0) continue;
    store.save(t.roomId, t.adminToken);
    saved += 1;
  }
  return saved;
};

/* -----------------------------------------------------------------------------
 * splitRoomsByEmailBinding — 2026-05-23 W2 公開前追加
 *
 * AdminPage の「メアド紐付け済」「メアド未紐付け」2 セクション分割の判定 helper.
 *
 * 入力:
 *   - `boundRooms`     : `GET /admin/rooms` の結果 (= owner_email == session.email).
 *                        既にメアドに紐付け済のルーム.
 *   - `tokenLookups`   : `POST /admin/rooms/by-tokens` の `active` バケツ.
 *                        localStorage に admin_token を持っている active ルーム
 *                        全部 (紐付け済 + 未紐付け 両方を含む).
 *
 * 出力:
 *   - `bound`   : `boundRooms` をそのまま渡す (= 既存 RoomList の入力互換).
 *   - `unbound` : `tokenLookups` から `boundRooms.id` を除外した残り (= 未紐付け).
 *
 * 設計判断:
 *   - `lookupRoomsByTokens` のレスポンスには現状 `ownerEmail` が含まれていないので、
 *     紐付け済 / 未紐付けの判定は **id 集合の差分** で実装する.
 *   - server に新しい endpoint を追加せず、既存 2 endpoint の組み合わせで完結する.
 *   - 削除済ルーム (`deleted` バケツ) は本ページの 2 セクションには出さない
 *     (= HomePage 「あなたのルーム」側で扱う). 本 helper は active のみ受ける.
 *
 * Pure function. AdminPage.tsx 側はこの helper の出力をそのまま 2 セクションに
 * 流すだけ.
 * --------------------------------------------------------------------------- */

export interface SplitRoomsByEmailBindingResult {
  /** owner_email == session.email のルーム (= 既存「紐付け済」セクション). */
  readonly bound: readonly AdminRoomSummary[];
  /**
   * `adminToken` を持っているが owner_email が未設定 (or 別 email) の active ルーム.
   * 「📧 メアドに紐付ける」ボタンを出して 1 クリックで紐付ける UI 用.
   */
  readonly unbound: readonly ActiveRoomLookupResult[];
}

export const splitRoomsByEmailBinding = (
  boundRooms: readonly AdminRoomSummary[],
  tokenLookups: readonly ActiveRoomLookupResult[],
): SplitRoomsByEmailBindingResult => {
  const boundIds = new Set<string>();
  for (const r of boundRooms) boundIds.add(r.id);
  const unbound: ActiveRoomLookupResult[] = [];
  for (const t of tokenLookups) {
    if (boundIds.has(t.roomId)) continue;
    unbound.push(t);
  }
  return { bound: boundRooms, unbound };
};

/* -----------------------------------------------------------------------------
 * bindSessionEmail outcome → AdminPage 側 state
 *
 * 「📧 メアドに紐付ける」ボタン押下後の結果を AdminPage のメッセージバーに出すための
 * 文言マッピング. 既存 mapDeleteRoomOutcome 等と同 shape (= 'ok' / 'unauthorized' /
 * 'unknown' などへ snake_case で集約).
 *
 *   - 'ok'           : 成功. 一覧再取得 (refresh) のトリガ.
 *   - 'unauthorized' : cookie session 切れ → /login へ navigate (既存と同動線).
 *   - 'forbidden'    : admin_token が URL :id と不一致 (壊れた localStorage state).
 *                      UI 側では「ローカルの認証情報が古い可能性があります。再読込してください。」
 *   - 'not_found'    : room が既に削除されている. UI 側では「対象が見つかりません」.
 *   - 'conflict'     : 既に別の email に紐付けられている. UI 側では「既にメアド紐付け済」.
 *   - 'rate_limited' : 429. UI 側では「少し時間を置いてからお試しください」.
 *   - 'unknown'      : network / 5xx / invalid body. UI 側では「紐付けに失敗しました。再読込してください」.
 * --------------------------------------------------------------------------- */

const BIND_SESSION_EMAIL_FORBIDDEN_MESSAGE =
  'ローカルの認証情報が古い可能性があります。ページを再読込してからお試しください。';
const BIND_SESSION_EMAIL_CONFLICT_MESSAGE =
  '既に別のメアドに紐付けられています。一覧を再読込してください。';
const BIND_SESSION_EMAIL_UNKNOWN_MESSAGE =
  '紐付けに失敗しました。少し時間を置いてからページを再読込してお試しください。';

export interface BindSessionEmailResult {
  readonly outcome:
    | 'ok'
    | 'unauthorized'
    | 'forbidden'
    | 'conflict'
    | 'not_found'
    | 'rate_limited'
    | 'unknown';
  readonly message: string;
  /** ok 時のみ — server から返ってきた紐付け先 email. */
  readonly ownerEmail?: string;
}

export const mapBindSessionEmailOutcome = (
  out: BindSessionEmailOutcome,
): BindSessionEmailResult => {
  switch (out.outcome) {
    case 'ok':
      return {
        outcome: 'ok',
        message: `「${out.ownerEmail}」に紐付けました。`,
        ownerEmail: out.ownerEmail,
      };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'forbidden':
      return { outcome: 'forbidden', message: BIND_SESSION_EMAIL_FORBIDDEN_MESSAGE };
    case 'not-found':
      return { outcome: 'not_found', message: NOT_FOUND_MESSAGE };
    case 'conflict':
      return { outcome: 'conflict', message: BIND_SESSION_EMAIL_CONFLICT_MESSAGE };
    case 'rate-limited':
      return {
        outcome: 'rate_limited',
        message: `操作制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      // server raw message ("HTTP 500" / "network error" 等) はユーザ向けには
      // 役に立たないので、専用の reload 案内文を優先する.
      return {
        outcome: 'unknown',
        message: BIND_SESSION_EMAIL_UNKNOWN_MESSAGE,
      };
  }
};
